import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Typography, Menu, MenuItem, ListItemIcon, ListItemText, IconButton, Tooltip, Divider } from '@mui/material';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DuplicateCategoryAsDocumentDialog from '../dialogs/DuplicateCategoryAsDocumentDialog';
import { updateCategoryStructure } from '../../utils/CategoryUtils';
import { TreeView, TreeItem, TreeItemProps, treeItemClasses } from '@mui/x-tree-view';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useHelpContent } from '../../utils/HelpUtils';

const TreeContainer = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  position: 'relative',
  zIndex: 0,
  padding: '16px',
  overflow: 'auto', // Enable scrolling
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
    '&:hover': {
      background: '#555',
    },
  },
});

interface StyledCategoryLabelProps {
  isSelected?: boolean;
}

const CategoryLabel = styled(Box)<StyledCategoryLabelProps>(({ isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '4px 8px',
  cursor: 'pointer',
  backgroundColor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));

const CategoryName = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexGrow: 1,
});

const CategoryActions = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '2px',
  opacity: 1,
  flexShrink: 0,
  '& .MuiIconButton-root': {
    padding: '2px',
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  },
  '& .MuiSvgIcon-root': {
    fontSize: '16px',
  },
});

const StyledTreeItem = styled(TreeItem)(({ theme }) => ({
  '& .MuiTreeItem-content': {
    padding: '0px 4px',
    minHeight: '24px',
    margin: '1px 0',
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  '& .MuiTreeItem-group': {
    marginLeft: 0,
    paddingLeft: theme.spacing(2),
  },
  '& .MuiTreeItem-label': {
    paddingLeft: theme.spacing(1),
  },
  '& .MuiTreeItem-iconContainer': {
    padding: '0px',
    marginRight: '4px',
  },
}));

const DropIndicator = styled('div')({
  height: '2px',
  backgroundColor: '#1976d2',
  position: 'absolute',
  left: 0,
  right: 0,
  pointerEvents: 'none',
});


interface CategoryTreeProps {
  documentName: string;
  categories: Category[];
  selectedCategoryId: string | null;
  onCategorySelect: (category: Category | null) => void;
  onCategoriesChange: (categories: Category[]) => void;
  onSplitCategory: () => void;
  onAddCategory: () => void;
  onAddContent: () => void;
  onDeleteCategory: () => void;
  onFlattenCategory: () => void;
  onRenameCategory: () => void;
  onMoveCategory: (direction: 'up' | 'down') => void;
  onCutCategory: () => void;
  onCopyCategory: () => void;
  onPasteCategory: () => void;
  canAddCategory: (category: Category | null) => boolean;
  canDeleteCategory: (category: Category | null) => boolean;
  canFlattenCategory: (category: Category | null) => boolean;
  canMoveCategory: (category: Category | null, direction: 'up' | 'down') => boolean;
  canCutCategory: (category: Category | null) => boolean;
  canCopyCategory: (category: Category | null) => boolean;
  canPasteCategory: (category: Category | null) => boolean;
  canSplitCategory: boolean;
  canRenameCategory: (category: Category | null) => boolean;
  clipboardCategory: Category | null;
  clipboardContents: Content [];
  expandedNodes: string[];
  onNodeToggle: (nodeIds: string[]) => void;
  onDragStart?: (category: Category) => void;
  onDragOver?: (category: Category) => void;
  onDropCategory?: (sourceCategory: Category, targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => void;
  onDropContents?: (contents: Content[], targetCategory: Category, position: 'before' | 'after' | 'inside', copy: boolean) => void;
  category: Category;
  onCategoryMultiSelect: (category: Category, event: React.MouseEvent) => void;
  selectedCategories:  Category[];
  onGenerateSubtree: () => void;
  canGenerateSubtree: (category: Category | null) => boolean;
  onShowSubtree: () => void;
}

export const CategoryTree = forwardRef<HTMLDivElement, CategoryTreeProps>(({
  documentName,
  categories,
  selectedCategoryId,
  onCategorySelect,
  onCategoriesChange,
  onSplitCategory,
  onAddCategory,
  onAddContent,
  onDeleteCategory,
  onFlattenCategory,
  onRenameCategory,
  onMoveCategory,
  onCutCategory,
  onCopyCategory,
  onPasteCategory,
  canAddCategory,
  canDeleteCategory,
  canFlattenCategory,
  canMoveCategory,
  canCutCategory,
  canCopyCategory,
  canPasteCategory,
  canSplitCategory,
  canRenameCategory,
  clipboardCategory,
  clipboardContents,
  expandedNodes,
  onNodeToggle,
  onDragStart,
  onDragOver,
  onDropCategory,
  onDropContents,
  category,
  onCategoryMultiSelect,
  selectedCategories,
  onGenerateSubtree,
  canGenerateSubtree,
  onShowSubtree
}, ref): JSX.Element => {
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    category: Category | null;
  } | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const { getMenuTooltipTextForState } = useHelpContent();

  const dragRef = useRef<string | null>(null);
  const dragOverRef = useRef<string | null>(null);

  const handleNodeSelect = (event: React.SyntheticEvent, nodeId: string) => {
    const category = findCategoryById(categories, nodeId);
    if (category) {
      onCategorySelect(category);
    }
  };

  const findCategory = (cats: Category[], id: string): Category | null => {
    if (!cats || !Array.isArray(cats) || !id) return null;

      for (const cat of cats) {
      if (cat.id === id) return cat;
        if (cat.children) {
        const found = findCategory(cat.children, id);
        if (found) return found;
        }
      }
      return null;
    };

  const findCategoryById = (cats: Category[], id: string): Category | null => {
    if (!cats || !Array.isArray(cats) || !id) return null;

    for (const cat of cats) {
      if (cat.id === id) return cat;
      if (cat.children) {
        const found = findCategoryById(cat.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const handleContextMenu = (event: React.MouseEvent, category: Category | null) => {
    event.preventDefault();
    
    // Set the selected category to the right-clicked category before opening context menu
    if (category) {
      onCategorySelect(category);
    }
    
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
            category,
          }
        : null
    );
  };
  const handleContextMenuClose = () => {
    setContextMenu(null);
  };

  const handleContextMenuAction = (action: () => void) => {
    handleContextMenuClose();
    action();
  };

  const handleClick = (event: React.MouseEvent, category: Category) => {
    event.stopPropagation();
    onCategorySelect(category);
  };

  const handleSelect = (event: React.MouseEvent, category: Category) => {
    event.stopPropagation();
    onCategoryMultiSelect(category, event);
  };

  const CategoryNode: React.FC<{ category: Category; level?: number }> = ({ category, level = 0 }) => {
    const isSelected = selectedCategoryId === category.id;
    const hasChildren = category.children && category.children.length > 0;

    type CategoryDragItem = {
      type: 'CATEGORY';
      id: string;
    };

    type CategoryRowDragItem = {
      type: 'CATEGORY_ROW';
      categories: Category[];
    };

    type ContentRowDragItem = {
      type: 'CONTENT_ROW';
      contents: Content[];
    };

    type DragItem = CategoryDragItem | CategoryRowDragItem |ContentRowDragItem;

    const [dropTarget, setDropTarget] = useState<{
      categoryId: string;
      position: 'before' | 'after' | 'inside';
    } | null>(null);

    const [{ isDragging }, drag] = useDrag({
      type: 'CATEGORY',
      canDrag: () => { return category.parentId != null },
      item: () => {
            return { type: 'CATEGORY', id: category.id };
          },
      end: () => setDropTarget(null),
      collect: (monitor) => {
          return {
            isDragging: monitor.isDragging(),
          };
      },
    });

   const ref = useRef<HTMLDivElement | null>(null);
   const [isAltPressed, setIsAltPressed] = useState(false);

   const [{ isOver, itemType }, drop] = useDrop<DragItem, void, { isOver: boolean; itemType: string | null }>({
     accept: ['CATEGORY', 'CATEGORY_ROW', 'CONTENT_ROW'],
     drop(item: DragItem, monitor: DropTargetMonitor<DragItem, void>) {
       if (!dropTarget) return;
       const targetCategory = findCategoryById(categories, dropTarget.categoryId);
       const type = monitor.getItemType();
       if (item.type === 'CATEGORY') {
            if (!dropTarget || item.id === category.id) return;
            const sourceCategory = findCategoryById(categories, item.id);

           if (sourceCategory && targetCategory) {
             onDropCategory?.(sourceCategory, targetCategory, dropTarget.position, isAltPressed);
           }
       }
       else if (item.type === 'CATEGORY_ROW') {
           if (!dropTarget || item.categories.length > 1 || !item.categories[0].contentCategory) return;
           console.log(item);
           const sourceCategory = findCategoryById(categories, item.categories[0].id);

          if (sourceCategory && targetCategory) {
            onDropCategory?.(sourceCategory, targetCategory, dropTarget.position, isAltPressed);
          }
      }
       else if (item.type === 'CONTENT_ROW') {
           if (targetCategory) {
             onDropContents?.(item.contents, targetCategory, dropTarget.position, isAltPressed);
           }
       }

       setDropTarget(null);
     },
     canDrop(item: DragItem, monitor: DropTargetMonitor<DragItem, void>) {
       if (!dropTarget) return false;
       const targetCategory = findCategoryById(categories, dropTarget.categoryId);
       if (!targetCategory) return false;

       if ( !dropTarget.position ) return false;

       if (item.type === 'CATEGORY') {
            const sourceCategory = findCategoryById(categories, item.id);
            if (!sourceCategory || !sourceCategory.parentId) return false;

            const parentCategory = findCategoryById(categories, sourceCategory.parentId);
            if (!parentCategory) return false;

            return  dropTarget.position === 'inside' &&
                targetCategory.id !== sourceCategory.id &&
                sourceCategory.parentId !== targetCategory.id &&
                !targetCategory.contentCategory &&
                (parentCategory.children!.length > 2 || isAltPressed ) &&
                targetCategory.children!.length > 1 ||
                (dropTarget.position === 'before' || dropTarget.position === 'after') &&
                sourceCategory.parentId === targetCategory.parentId;
       }
       else  if (item.type === 'CATEGORY_ROW') {
            if (item.categories.length > 1 || !item.categories[0].contentCategory) return false;
            const sourceCategory = findCategoryById(categories, item.categories[0].id);
            if (!sourceCategory || !sourceCategory.parentId) return false;

            const parentCategory = findCategoryById(categories, sourceCategory.parentId);
            if (!parentCategory) return false;

            return  dropTarget.position === 'inside' &&
                targetCategory.id !== sourceCategory.id &&
                sourceCategory.parentId !== targetCategory.id &&
                !targetCategory.contentCategory &&
                (parentCategory.children!.length > 2 || isAltPressed ) &&
                targetCategory.children!.length > 1;

       }
       else if (item.type === 'CONTENT_ROW') {
           return dropTarget.position === 'inside' &&
                  targetCategory.contentCategory;
       }
       return false;
     },
     hover(item: DragItem, monitor: DropTargetMonitor<DragItem, void>) {
       if (!ref.current) return;

         const isOver = monitor.isOver({ shallow: true });
         if (!isOver) {
           setDropTarget(null);
           return;
         }

       const clientOffset = monitor.getClientOffset();
       const boundingRect = ref.current?.getBoundingClientRect();
       if (!clientOffset || !boundingRect) return;

       const y = clientOffset.y - boundingRect.top;
       const height = boundingRect.height;

       let position: 'before' | 'after' | 'inside' = 'inside';
       if (y < height / 3) position = 'before';
       else if (y > (2 * height) / 3) position = 'after';

       setDropTarget({ categoryId: category.id, position });
     },
     collect: (monitor) => ({
      isOver: monitor.isOver(),
      itemType: monitor.getItemType() as string | null,
     }),
   });

   useEffect(() => {
     if (!isOver) {
       setDropTarget((prev) => {
         // Only clear if this item owns the current dropTarget
         if (prev?.categoryId === category.id) {
           return null;
         }
         return prev;
       });
     }
   }, [isOver, category.id]);


   useEffect(() => {
     const handleKeyDown = (e: KeyboardEvent) => {
       if (e.altKey) setIsAltPressed(true);
     };
     const handleKeyUp = (e: KeyboardEvent) => {
       if (!e.altKey) setIsAltPressed(false);
     };

     window.addEventListener('keydown', handleKeyDown);
     window.addEventListener('keyup', handleKeyUp);

     return () => {
       window.removeEventListener('keydown', handleKeyDown);
       window.removeEventListener('keyup', handleKeyUp);
     };
   }, []);

    return (
      <TreeItem
        onFocusCapture={(e) => e.stopPropagation()}
        key={category.id}
        nodeId={category.id}
        data-category-id={category.id}
        label={
          <Box
              ref={(node: HTMLDivElement | null) => {
                  ref.current = node; // This assigns the ref
                  if (node) drag(drop(node)); // Attach DnD
              }}
              data-category-id={category.id}
              onClick={(e) => handleClick(e, category)}
              onContextMenu={(e) => handleContextMenu(e, category)}
              sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  cursor: 'grab',
                  '&:active': { cursor: 'grabbing' },
                  padding: '4px 8px',
                  borderRadius: '4px',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                  borderTop:
                    dropTarget?.categoryId === category.id && dropTarget?.position === 'before'
                      ? '1px solid #64b5f6'
                      : undefined,
                  borderBottom:
                    dropTarget?.categoryId === category.id && dropTarget?.position === 'after'
                      ? '1px solid #64b5f6'
                      : undefined,
                  backgroundColor:
                    dropTarget?.categoryId === category.id && dropTarget?.position === 'inside'
                      ? 'rgba(25, 118, 210, 0.1)'
                      : undefined,
                }}
            >
              {hasChildren ? (
                <FolderIcon sx={{ color: '#2c3e50', mr: 1 }} />
              ) : (
                <DescriptionIcon sx={{ color: '#2c3e50', mr: 1 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: isSelected ? '#2c3e50' : 'inherit',
                  fontWeight: isSelected ? 'bold' : 'normal',
                  fontSize: 'var(--app-font-size, 14px)',
                }}
              >
                {category.name}
              </Typography>
            </Box>
        }
      >
        {hasChildren &&
          category.children?.map((child) => (
            <CategoryNode key={child.id} category={child} level={level + 1} />
          ))}
      </TreeItem>
    );
  };

  const handleNodeToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
    // Find the node that was toggled by comparing with previous expanded nodes
    const toggledNodeId = nodeIds.find(id => !expandedNodes.includes(id)) || 
                         expandedNodes.find(id => !nodeIds.includes(id));
    
    if (!toggledNodeId) return;

    if (expandedNodes.includes(toggledNodeId)) {
      // If the node was expanded before, it's being collapsed
      onNodeToggle(expandedNodes.filter(id => id !== toggledNodeId));
    } else {
      // If the node was collapsed before, it's being expanded
      onNodeToggle([...expandedNodes, toggledNodeId]);
    }
  };

  return (
    <>
      <TreeContainer ref={ref}>
          <TreeView
            defaultCollapseIcon={<ExpandMoreIcon />}
            defaultExpandIcon={<ChevronRightIcon />}
            expanded={expandedNodes}
            selected={selectedCategoryId || ''}
            onNodeSelect={handleNodeSelect}
            onNodeToggle={handleNodeToggle}
            sx={{
              fontSize: 'var(--app-font-size, 14px)',
              '& .MuiTreeItem-content': {
                padding: '0px 4px',
                minHeight: '24px',
                margin: '1px 0',
                '&:hover': {
                  backgroundColor: 'transparent !important',
                },
                '&.Mui-focused': {
                  backgroundColor: 'transparent !important',
                },
                '&.Mui-selected': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08) !important',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08) !important',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08) !important',
                  },
                },
              },
              '& .MuiTreeItem-group': {
                marginLeft: 0,
                paddingLeft: theme => theme.spacing(2),
              },
              '& .MuiTreeItem-label': {
                paddingLeft: theme => theme.spacing(1),
                fontSize: 'var(--app-font-size, 14px)',
              },
              '& .MuiTreeItem-iconContainer': {
                padding: '0px',
                marginRight: '4px',
              },
            }}
          >
            {categories.map((cat) => (
              <CategoryNode key={cat.id} category={cat} />
            ))}
          </TreeView>
        <Menu
          open={contextMenu !== null}
          onClose={handleContextMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <Tooltip title={getMenuTooltipTextForState('split-category', !!(contextMenu?.category && contextMenu?.category.contentCategory))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onSplitCategory();
                }}
                disabled={!contextMenu?.category || !contextMenu?.category.contentCategory}
              >
                <ListItemIcon>
                  <CallSplitIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Split Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('add-category', !!(contextMenu?.category && canAddCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onAddCategory();
                }}
                disabled={!contextMenu?.category || !canAddCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
           <Tooltip title={getMenuTooltipTextForState('add-content', !!(contextMenu?.category && contextMenu?.category.contentCategory))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  onAddContent?.();
                  handleContextMenuClose();
                }}
                disabled={!contextMenu?.category || !contextMenu?.category.contentCategory}
              >
                <ListItemIcon>
                  <AddIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Add Content</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('delete-category', !!(contextMenu?.category && canDeleteCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onDeleteCategory();
                }}
                disabled={!contextMenu?.category || !canDeleteCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Delete Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
           <Tooltip title={getMenuTooltipTextForState('flatten-category', !!(contextMenu?.category && canFlattenCategory(contextMenu.category)))} placement="right">
              <span>
                <MenuItem
                  onClick={() => {
                    handleContextMenuClose();
                    onFlattenCategory();
                  }}
                  disabled={!contextMenu?.category || !canFlattenCategory(contextMenu.category)}
                >
                  <ListItemIcon>
                    <KeyboardDoubleArrowUpIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Flatten Category</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('rename-category', !!(contextMenu?.category && canRenameCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onRenameCategory();
                }}
                disabled={!contextMenu?.category || !canRenameCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Rename Category</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('move-category-up', !!(contextMenu?.category && canMoveCategory(contextMenu.category, 'up')))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onMoveCategory('up');
                }}
                disabled={!contextMenu?.category || !canMoveCategory(contextMenu.category, 'up')}
              >
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Up</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('move-category-down', !!(contextMenu?.category && canMoveCategory(contextMenu.category, 'down')))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onMoveCategory('down');
                }}
                disabled={!contextMenu?.category || !canMoveCategory(contextMenu.category, 'down')}
              >
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Move Down</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('cut-category', !!(contextMenu?.category && canCutCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onCutCategory();
                }}
                disabled={!contextMenu?.category || !canCutCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <ContentCutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Cut</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('copy-category', !!(contextMenu?.category && canCopyCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onCopyCategory();
                }}
                disabled={!contextMenu?.category || !canCopyCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <ContentCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Copy</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('paste-category', !!(contextMenu?.category && canPasteCategory(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onPasteCategory();
                }}
                disabled={!contextMenu?.category || !canPasteCategory(contextMenu.category)}
              >
                <ListItemIcon>
                  <ContentPasteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Paste</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Divider />
          <Tooltip title={getMenuTooltipTextForState('duplicate-as-document', !!contextMenu?.category)} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  setDuplicateDialogOpen(true);
                }}
                disabled={!contextMenu?.category}
              >
                <ListItemIcon>
                  <FileCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Duplicate as Document</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
          <Tooltip title={getMenuTooltipTextForState('generate-subtree', !!(contextMenu?.category && canGenerateSubtree(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onGenerateSubtree();
                }}
                disabled={!contextMenu?.category || !canGenerateSubtree(contextMenu.category)}
              >
                <ListItemIcon>
                  <AutoAwesomeIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Generate Subtree from Content</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
         <Tooltip title={getMenuTooltipTextForState('show-sub-tree', !!(contextMenu?.category && canGenerateSubtree(contextMenu.category)))} placement="right">
            <span>
              <MenuItem
                onClick={() => {
                  handleContextMenuClose();
                  onShowSubtree();
                }}
                disabled={!contextMenu?.category}
              >
                <ListItemIcon>
                  <VisibilityIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Show Subtree</ListItemText>
              </MenuItem>
            </span>
          </Tooltip>
        </Menu>
      </TreeContainer>
      <DuplicateCategoryAsDocumentDialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        onConfirm={(newName, selectedCategories, selectedSubcases) => {
          // TODO: Implement the logic to create the new document and add categories/content
          console.log('Duplicate as document:', { newName, selectedCategories, selectedSubcases });
        }}
        selectedCategory={contextMenu?.category || (categories.length > 0 ? categories[0] : null)}
        categories={categories}
        existingDocumentNames={[]} // TODO: Pass actual document names
        documentId={''} // TODO: Pass actual document ID
        subcaseGroups={[]} // CategoryTree doesn't have access to document subcase groups
        documentSubcases={[]} // CategoryTree doesn't have access to document subcases
      />
    </>
  );
});

export default CategoryTree; 